import { Alert, Button, DatePicker, Grid, Select, Space, Table, Tag, Typography } from "antd";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { type Dayjs } from "dayjs";
import { api } from "../app/api.ts";
import { notificationKeys, useMessageTemplates, type DeliveryLogEntry, type OutboxMessageStatus } from "../app/notifications.ts";
import { formatSchoolDateTime, SCHOOL_TIME_ZONE } from "../app/time.ts";

const PAGE_SIZE = 25;

const statusColor: Record<OutboxMessageStatus, string> = {
  Pending: "blue",
  Sent: "green",
  Failed: "red",
  Unreachable: "orange",
  Skipped: "default",
};

const statuses = Object.keys(statusColor) as OutboxMessageStatus[];

/** The Telegram delivery log (story 119, delivery part) from GET /notifications/messages. Requires notifications.manage. */
export function DeliveryLogPage() {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const templates = useMessageTemplates();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<OutboxMessageStatus | undefined>(undefined);
  const [templateKey, setTemplateKey] = useState<string | undefined>(undefined);
  const [range, setRange] = useState<[Dayjs, Dayjs] | undefined>(undefined);

  // Whole school days in Tashkent: from the start of the first day to the start of the day after the last.
  const from = range ? dayjs.tz(range[0].format("YYYY-MM-DD"), SCHOOL_TIME_ZONE).toISOString() : undefined;
  const to = range ? dayjs.tz(range[1].format("YYYY-MM-DD"), SCHOOL_TIME_ZONE).add(1, "day").toISOString() : undefined;
  const filters = { status, templateKey, from, to, page };

  const query = useQuery({
    queryKey: notificationKeys.log(filters),
    queryFn: async () => {
      const { data, error } = await api.GET("/notifications/messages", {
        params: {
          query: {
            Page: page,
            PageSize: PAGE_SIZE,
            ...(status === undefined ? {} : { Status: status }),
            ...(templateKey === undefined ? {} : { TemplateKey: templateKey }),
            ...(from === undefined ? {} : { From: from }),
            ...(to === undefined ? {} : { To: to }),
          },
        },
      });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    placeholderData: keepPreviousData,
  });

  function resetPage<T>(set: (value: T) => void) {
    return (value: T) => {
      set(value);
      setPage(1);
    };
  }

  const missing = <Typography.Text type="secondary">—</Typography.Text>;
  const columns = [
    { key: "time", title: t("notifications.log.columns.time"), width: 140, render: (_: unknown, m: DeliveryLogEntry) => formatSchoolDateTime(m.createdAt) },
    { key: "template", title: t("notifications.log.columns.template"), width: 180, render: (_: unknown, m: DeliveryLogEntry) => <Typography.Text code>{m.templateKey}</Typography.Text> },
    {
      key: "recipient",
      title: t("notifications.log.columns.recipient"),
      width: 180,
      render: (_: unknown, m: DeliveryLogEntry) => (
        <Space orientation="vertical" size={0}>
          {m.recipientKind ? <Typography.Text>{t(`notifications.log.recipientKinds.${m.recipientKind}`)}</Typography.Text> : null}
          <Typography.Text type="secondary">{t("notifications.log.chat", { id: String(m.chatId) })}</Typography.Text>
        </Space>
      ),
    },
    { key: "status", title: t("notifications.log.columns.status"), width: 150, render: (_: unknown, m: DeliveryLogEntry) => <Tag color={statusColor[m.status]}>{t(`notifications.log.statuses.${m.status}`)}</Tag> },
    { key: "detail", title: t("notifications.log.columns.detail"), render: (_: unknown, m: DeliveryLogEntry) => <Detail entry={m} missing={missing} /> },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("notifications.log.title")}
        </Typography.Title>
        <Typography.Text type="secondary">{t("notifications.log.subtitle")}</Typography.Text>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <Select<OutboxMessageStatus | undefined>
          allowClear
          value={status}
          placeholder={t("notifications.log.allStatuses")}
          aria-label={t("notifications.log.statusFilter")}
          style={{ width: screens.md ? 200 : "100%" }}
          options={statuses.map((s) => ({ value: s, label: t(`notifications.log.statuses.${s}`) }))}
          onChange={resetPage(setStatus)}
        />
        <Select<string | undefined>
          allowClear
          showSearch
          value={templateKey}
          placeholder={t("notifications.log.allTemplates")}
          aria-label={t("notifications.log.templateFilter")}
          loading={templates.isPending}
          style={{ width: screens.md ? 260 : "100%" }}
          options={(templates.data ?? []).map((tpl) => ({ value: tpl.key, label: tpl.key }))}
          onChange={resetPage(setTemplateKey)}
        />
        <DatePicker.RangePicker
          value={range ?? null}
          format="DD.MM.YYYY"
          aria-label={t("notifications.log.range")}
          style={{ width: screens.md ? 280 : "100%" }}
          onChange={(values) => {
            setRange(values?.[0] && values[1] ? [values[0], values[1]] : undefined);
            setPage(1);
          }}
        />
      </div>
      {query.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />}
      <Table<DeliveryLogEntry>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={query.isPending || (query.isFetching && query.isPlaceholderData)}
        columns={columns}
        dataSource={query.data?.items ?? []}
        locale={{ emptyText: t("notifications.log.empty") }}
        scroll={{ x: 900 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: Number(query.data?.totalCount ?? 0),
          showSizeChanger: false,
          hideOnSinglePage: true,
          showTotal: (total: number) => t("notifications.log.total", { count: total }),
          onChange: setPage,
        }}
      />
    </Space>
  );
}

function Detail({ entry, missing }: { entry: DeliveryLogEntry; missing: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <Space orientation="vertical" size={2} style={{ width: "100%" }}>
      {entry.text ? <Typography.Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }} ellipsis={{ rows: 2, expandable: true }}>{entry.text}</Typography.Paragraph> : missing}
      {entry.errorCode && <Typography.Text type="danger">{t(`notifications.log.errors.${entry.errorCode}`, { defaultValue: entry.errorCode })}</Typography.Text>}
      <Typography.Text type="secondary">
        {t("notifications.log.attempts", { count: Number(entry.attempts) })}
        {entry.status === "Pending" ? ` · ${t("notifications.log.nextAttempt", { time: formatSchoolDateTime(entry.nextAttemptAt) })}` : ""}
        {entry.sentAt ? ` · ${t("notifications.log.sentAt", { time: formatSchoolDateTime(entry.sentAt) })}` : ""}
      </Typography.Text>
    </Space>
  );
}
